<template>
    <footer class="footer-bg text-white">
        <div class="divider"></div>
        <div
            class="w-full h-[395px] flex flex-col lg:flex-row items-stretch justify-between px-8 lg:px-12 footer-inner"
        >
            <!-- LEFT COLUMN -->
            <div
                class="flex flex-col justify-between items-center lg:items-start text-center lg:text-left h-full py-10 lg:py-12 footer-column"
            >
                <app-image
                    url="logo.png"
                    is-local
                    alt="Pinguinitos logo"
                    width="196"
                    height="66"
                ></app-image>

                <p
                    class="footer-text font-primary text-lg leading-snug max-w-md mt-4 mb-6"
                >
                    {{ $config("FOOTER_SLOGAN") }}
                </p>

                <!-- MOBILE ABOUT | CONTACT -->
                <div
                    v-if="isMobile"
                    class="flex items-center gap-3 footer-link-row my-4"
                >
                    <router-link class="footer-link" :to="{ name: 'about' }">
                        {{ $config("FOOTER_ABOUT") }}
                    </router-link>
                    <span>|</span>
                    <router-link class="footer-link" :to="{ name: 'contact' }">
                        {{ $config("FOOTER_CONTACT") }}
                    </router-link>
                </div>

                <!-- SOCIAL MEDIA ICONS -->
                <div
                    class="flex items-center justify-center lg:justify-start gap-6"
                >
                    <a
                        href="https://www.facebook.com/pinguinitostv"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                    >
                        <app-image
                            url="new/facebook.png"
                            is-local
                            alt="Facebook"
                            class="social-icon"
                        ></app-image>
                    </a>
                    <a
                        href="https://www.instagram.com/pinguinitos.tv/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                    >
                        <app-image
                            url="new/instagram.png"
                            is-local
                            alt="Instagram"
                            class="social-icon"
                        ></app-image>
                    </a>
                    <a
                        href="https://twitter.com/Pinguinitos_TV"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="X"
                    >
                        <app-image
                            url="new/x.png"
                            is-local
                            alt="X"
                            class="social-icon"
                        ></app-image>
                    </a>
                    <a
                        href="https://www.youtube.com/pinguinitos/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="YouTube"
                    >
                        <app-image
                            url="new/youtube.png"
                            is-local
                            alt="YouTube"
                            class="social-icon"
                        ></app-image>
                    </a>
                </div>
            </div>

            <!-- RIGHT COLUMN -->
            <div
                class="flex flex-col justify-between items-center lg:items-start text-center lg:text-left h-full pb-10 lg:py-12 footer-column"
            >
                <!-- DESKTOP ABOUT | CONTACT -->
                <div
                    v-if="!isMobile"
                    class="flex items-center gap-3 footer-link-row"
                >
                    <router-link class="footer-link" :to="{ name: 'about' }">
                        {{ $config("FOOTER_ABOUT") }}
                    </router-link>
                    <span>|</span>
                    <router-link class="footer-link" :to="{ name: 'contact' }">
                        {{ $config("FOOTER_CONTACT") }}
                    </router-link>
                </div>

                <!-- POLICIES -->
                <div
                    class="footer-policies"
                    :class="{
                        'my-4': !isMobile,
                        'py-0': isMobile,
                        'w-80vw': isMobile
                    }"
                >
                    <router-link
                        class="footer-policy lg:my-4"
                        :to="{ name: 'privacy-policy' }"
                    >
                        {{ $config("PPRIVACIDAD") }}
                    </router-link>

                    <span v-if="isMobile" class="footer-divider">|</span>

                    <router-link
                        class="footer-policy"
                        :to="{ name: 'terms-of-use' }"
                    >
                        {{ $config("TERMINOS") }}
                    </router-link>
                </div>

                <span class="footer-copy mt-5 mb-3">
                    © {{ new Date().getFullYear() }} Pinguinitos. All rights
                    reserved.
                </span>
            </div>
        </div>
    </footer>
</template>

<script>
export default {
    name: "footer",

    data() {
        return {
            isMobile: false
        };
    },

    mounted() {
        this.checkDeviceType();
        window.addEventListener("resize", this.checkDeviceType);
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.checkDeviceType);
    },

    methods: {
        // Navigation switches at < 1024px (lg)
        checkDeviceType() {
            this.isMobile = window.innerWidth < 1024;
        }
    }
};
</script>

<style scoped>
/* === COLORS === */
.footer-bg {
    background-color: var(--color-footer, #025b8c);
}

.divider {
    width: 100vw;
    height: 10px;
    background: #ffffff;
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
}

/* === TYPOGRAPHY === */
.font-primary {
    font-family: "Encode Sans Semi Expanded", sans-serif;
    font-weight: 500;
}

/* === TEXT === */
.footer-text {
    text-align: center;
}
@media (min-width: 1024px) {
    .footer-text {
        text-align: left;
    }
}

/* === WIDTH UTILITY === */
.w-80vw {
    width: 80vw !important;
}

/* === SOCIAL ICONS === */
.social-icon {
    width: 40px;
    height: 43px;
    transition: transform 0.2s ease;
}
.social-icon:hover {
    transform: scale(1.08);
}
@media (min-width: 1024px) {
    .social-icon {
        width: 60px;
        height: 56px;
    }
}

/* === MAIN LINKS === */
.footer-link {
    color: #ffffff;
    text-decoration: underline;
    text-underline-offset: 2px;
    font-size: 1rem;
    transition: color 0.25s ease;
}
.footer-link:hover {
    color: var(--color-accent, #28d4ff);
}

/* === POLICIES === */
.footer-policies {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: 2px;
}

.footer-policy {
    color: #ffffff;
    font-size: 0.875rem;
    text-decoration: underline;
    line-height: 1.25rem;
    white-space: nowrap;
    transition: color 0.25s ease;
}

.footer-policy:hover {
    color: var(--color-accent, #28d4ff);
}

/* Divider */
.footer-divider {
    color: #ffffff;
    opacity: 0.8;
    margin: 0 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* === MOBILE INLINE POLICIES === */
@media (max-width: 1023px) {
    .footer-policies {
        flex-direction: row;
        justify-content: center;
        align-items: center;
        width: 80vw;
        text-align: center;
        gap: 0.5rem;
        margin: 0 auto;
    }
}

/* === COPYRIGHT === */
.footer-copy {
    font-size: 0.75rem;
    opacity: 0.85;
}

/* === LINK ROW === */
.footer-link-row {
    justify-content: center;
}
@media (min-width: 1024px) {
    .footer-link-row {
        justify-content: flex-start;
    }
}

/* === COLUMNS === */
.footer-column {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
}

.footer-inner {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;
    box-sizing: border-box;
}
</style>
